import {
  EnumTipoOperacion,
  apiFetchConfigOperacionBalanceByOperationType,
  EnumOperationState,
  Operacion,
  apiOperacionesPATCH,
  apiNominacionesGET,
  apiNominacionesDELETE,
  apiNominacionesPOST,
  apiNominacionesPATCH,
  EnumDispatchType,
  apiFlangesGET,
  apiNominacionesGETById,
  apiBatch,
  EnumTipoElemLog
} from '../../api'
import {
  AddMassiveNominationModel,
  FlangeViewModel,
  NominationListItemsV2Request
} from './nomination.model'
import {
  nominationListItemsMapperApiToVM,
  operationsBalanceConfigMapperAPItoModel,
  flangesItemMapperToVM,
  nominationMapperPOST,
  nominationMapperPATCH,
  nominationSendMapper,
  nominationsToDeleteBatchRequest,
  nominationsToCancelBatchRequest,
  nominationItemSendMapper,
  nominationToCancelRequest,
  nominationToDeleteRequest,
  mapCreateOrUpdateMassiveRequest
} from './nomination.mapper'
import { SelectOption } from '@globaleris/ui.library'
import {
  enumKeyToValue,
  enumValueToKey,
  enumEnumToValueString,
  enumEnumToValueNumber
} from '../../helpers'
import { DataTableActionConfiguration, ActionIcons, DatePeriod } from '../../model'
import i18n from '../../i18n'
import { getActiveBalanceZone, getActiveCompany, notifyError } from '../../redux'
import moment from 'moment'
import {
  createErrorServiceBatchResponse,
  createErrorServiceResponse,
  createGenericErrorServiceBatchResponse,
  createPioneerErrorServiceBatchResponse,
  createSuccessServiceBatchResponse,
  createSuccessServiceResponse
} from '../../helpers/serviceResponseHelper'
import { ServiceResponse } from '../../model/serviceResponse'
import NominationModel from './nomination.model'
import { BatchResponse } from '../../api/oData/responses'
import { ServiceBatchResponse } from '../../model/serviceBatchResponse'
import { getGasDay } from '../../helpers/gasDayHelper'
import { apiMassiveDispatchesPOST } from '../../api/Pioneer/endpoints/dispatches'
import {
  apiFetchCompatibleLogisticElementsSelectOptionsByOperationTypes,
  apiFetchLogisticElementsSelectOptionsByOperationTypes,
  apiFetchLogisticElementsSelectOptionsBySubtypes
} from '../../api/Pioneer/endpoints/logisticElements/logisticElements.api'
import {
  apiCreateOrUpdateMassiveOps,
  apiNominacionesGETV2
} from '../../api/Pioneer/endpoints/operations/operations.api'
import { HandleBatchResponse } from '../../api/Pioneer/model/responses/batch.response'

export const fetchNominationListItems = async (
  nominationTypes: EnumTipoOperacion[] | null,
  flangeTypes: EnumTipoOperacion[] | null,
  dateRange: DatePeriod,
  operationType?: EnumTipoOperacion,
  logisticElement?: number
): Promise<ServiceResponse<any>> => {
  return apiNominacionesGET(
    operationType ? [operationType] : nominationTypes,
    dateRange,
    getActiveBalanceZone(),
    getActiveCompany(),
    logisticElement,
    false
  )
    .then(async response => {
      if (response.length === 0) {
        return createSuccessServiceResponse(nominationListItemsMapperApiToVM([], []))
      }

      return fetchFlangesListItems(flangeTypes, response, dateRange)
        .then(flanges => {
          return createSuccessServiceResponse(nominationListItemsMapperApiToVM(response, flanges))
        })
        .catch(error => {
          return createErrorServiceResponse(error)
        })
    })
    .catch(error => {
      return createErrorServiceResponse(error)
    })
}

export const countNominationListItems = async (
  nominationTypes: EnumTipoOperacion[] | null,
  dateRange: DatePeriod,
  operationType?: EnumTipoOperacion,
  logisticElement?: number
): Promise<ServiceResponse<any>> => {
  return apiNominacionesGET(
    operationType ? [operationType] : nominationTypes,
    dateRange,
    getActiveBalanceZone(),
    getActiveCompany(),
    logisticElement,
    true
  )
    .then(async response => {
      return createSuccessServiceResponse(response.length)
    })
    .catch(error => {
      return createErrorServiceResponse(error)
    })
}

export const fetchNominationListItemsV2 = async ({
  nominationTypes,
  dateRange,
  operationTypes,
  logisticElement
}: NominationListItemsV2Request): Promise<ServiceResponse<any>> => {
  return apiNominacionesGETV2({
    operationTypes: operationTypes ? operationTypes : nominationTypes,
    dateRange: dateRange,
    balanceZone: getActiveBalanceZone(),
    company: getActiveCompany(),
    logisticElement: logisticElement
  })
    .then(async response => {
      return createSuccessServiceResponse(nominationListItemsMapperApiToVM(response, []))
    })
    .catch(error => {
      return createErrorServiceResponse(error)
    })
}

export const fetchFlangesListItems = async (
  flangeTypes: EnumTipoOperacion[] | null,
  listNominations: any[] | null,
  dateRange: DatePeriod
): Promise<any[]> => {
  const distinctLogistics = [...new Set(listNominations?.map(x => x.elementoLogistico.id))]

  return await apiFlangesGET(
    flangeTypes,
    distinctLogistics,
    dateRange,
    getActiveBalanceZone(),
    getActiveCompany()
  ).then(response => {
    return response
  })
}

export const fetchLogisticElementsToItems = async (
  serviceTypes: EnumTipoOperacion[]
): Promise<SelectOption[]> => {
  return await fetchLogisticElements(serviceTypes)
}

export const fetchLogisticElements = async (
  serviceTypes: EnumTipoOperacion[]
): Promise<SelectOption[]> => {
  const types = serviceTypes.map(type => enumValueToKey(EnumTipoOperacion, type))
  return await apiFetchLogisticElementsSelectOptionsBySubtypes(
    await apiFetchConfigOperacionBalanceByOperationType(types).then(response => {
      return operationsBalanceConfigMapperAPItoModel(response).map(
        item => item.logisticElementSubType
      )
    }),
    getActiveBalanceZone()?.id
  )
}

export const fetchCompatibleLogisticElementsSelectOption = async (
  operationTypes: EnumTipoOperacion[]
): Promise<SelectOption[]> => {
  const types = operationTypes
    .map(type => enumEnumToValueNumber(EnumTipoOperacion, type))
    .filter(x => x !== undefined) as number[]

  const balanceZone = getActiveBalanceZone()?.id ?? 1
  return await apiFetchCompatibleLogisticElementsSelectOptionsByOperationTypes(balanceZone, types)
}

export const fetchLogisticElementsSelectOption = async (
  operationTypes: EnumTipoOperacion[]
): Promise<SelectOption[]> => {
  const types = operationTypes
    .map(type => enumEnumToValueNumber(EnumTipoOperacion, type))
    .filter(x => x !== undefined) as number[]

  const balanceZone = getActiveBalanceZone()?.id ?? 1
  return await apiFetchLogisticElementsSelectOptionsByOperationTypes(balanceZone, types)
}

export const fetchFlangesItemsOfNomination = (
  operation: Operacion,
  flanges: Operacion[]
): FlangeViewModel[] => {
  return flanges
    ?.filter(
      x =>
        x.elementoLogistico.id === operation.elementoLogistico.id &&
        x.fechaOperacion === operation.fechaOperacion &&
        x.elementoLogistico.tipoElementoLogistico ===
          enumKeyToValue(EnumTipoElemLog, EnumTipoElemLog.Punto)
    )
    .map(fetchFlangesItemOfNomination)
}

export const fetchFlangesItemOfNomination = (operation: Operacion): FlangeViewModel => {
  return flangesItemMapperToVM(operation)
}

//#region SEND

export const canSendMassive = (items: NominationModel[]): boolean => {
  return items?.find(x => x.canSend === false) === undefined
}

export const canSend = (state: EnumOperationState, date: Date): boolean => {
  return (
    (state === EnumOperationState.Borrador ||
      state === EnumOperationState.PendienteRenvio ||
      state === EnumOperationState.Rechazado ||
      state === EnumOperationState.EnviadoKO) &&
    moment(date).isSameOrAfter(getGasDay(), 'day')
  )
}

export const send = async (item: NominationModel): Promise<boolean> => {
  return await apiMassiveDispatchesPOST(
    nominationItemSendMapper(
      item,
      moment(item.date).isBefore(getGasDay(), 'day')
        ? EnumDispatchType.Reparto02G // pasado
        : EnumDispatchType.Nominacion, // futuro
      getActiveCompany()?.id!
    )
  )
    .then(() => {
      return true
    })
    .catch(() => {
      notifyError(i18n.t('nomination:notifications.error.sendError'))
      return false
    })
}

export const sendMassive = async (operations: any[]): Promise<boolean> => {
  const nextOperations = operations.filter(x => moment(x.date).isSameOrAfter(getGasDay(), 'day'))
  let nextOperationsSuccess = true

  if (nextOperations.length > 0) {
    nextOperationsSuccess = await apiMassiveDispatchesPOST(
      nominationSendMapper(nextOperations, EnumDispatchType.Nominacion, getActiveCompany()?.id!)
    )
      .then(() => {
        return true
      })
      .catch(() => {
        notifyError(i18n.t('nomination:notifications.error.sendError'))
        return false
      })
  }
  return nextOperationsSuccess
}

//#endregion

//#region CANCEL

export const canCancelMassive = (items: any[]): boolean => {
  return items?.find(x => x.canCancel === false) === undefined
}

export const canCancel = (state: EnumOperationState, date: Date): boolean => {
  return (
    state !== EnumOperationState.Borrador &&
    state !== EnumOperationState.Anulada &&
    moment(date).isSameOrAfter(moment(), 'day')
  )
}

export const cancelMassive = async (items: NominationModel[]): Promise<ServiceBatchResponse> => {
  return await apiBatch(nominationsToCancelBatchRequest(items))
    .then((response: BatchResponse) => {
      if (response.responses.filter(x => x.status !== 204).length > 0) {
        return createErrorServiceBatchResponse(response.responses)
      }
      return createSuccessServiceBatchResponse()
    })
    .catch(error => {
      return createGenericErrorServiceBatchResponse(error)
    })
}

export const cancel = async (operation: any): Promise<ServiceResponse<any>> => {
  return apiOperacionesPATCH(nominationToCancelRequest(operation))
    .then(() => {
      return createSuccessServiceResponse(undefined)
    })
    .catch(error => {
      return createErrorServiceResponse(error)
    })
}

//#endregion

//#region DELETE

export const canDeleteMassive = (items: any[] | undefined): boolean => {
  return items?.find(x => x.canDelete === false) === undefined
}

export const canDelete = (state: EnumOperationState): boolean => {
  return state === EnumOperationState.Borrador || state === EnumOperationState.Anulada
}

export const deleteMassive = async (items: NominationModel[]): Promise<ServiceBatchResponse> => {
  return await apiBatch(nominationsToDeleteBatchRequest(items))
    .then((response: BatchResponse) => {
      if (response.responses.filter(x => x.status !== 204).length > 0) {
        return createErrorServiceBatchResponse(response.responses)
      }
      return createSuccessServiceBatchResponse()
    })
    .catch(error => {
      return createGenericErrorServiceBatchResponse(error)
    })
}

export const deleteNomination = async (
  nomination: any | undefined
): Promise<ServiceResponse<any>> => {
  return apiNominacionesDELETE(nominationToDeleteRequest(nomination))
    .then(() => {
      return createSuccessServiceResponse(undefined)
    })
    .catch(error => {
      return createErrorServiceResponse(error)
    })
}

//#endregion

//#region EDIT

export const canEdit = (state: EnumOperationState, date: Date): boolean => {
  return (
    moment(date).isSameOrAfter(moment(getGasDay()), 'day') && state !== EnumOperationState.Anulada
  )
}

export const editNomination = async (nomination: any): Promise<ServiceResponse<any>> => {
  if (nomination.state !== EnumOperationState.Borrador) {
    nomination.state = EnumOperationState.PendienteRenvio
  }
  return await apiNominacionesPATCH(
    nominationMapperPATCH(nomination),
    nomination.logisticElement.id
  )
    .then(() => {
      return createSuccessServiceResponse(undefined)
    })
    .catch(error => {
      return createErrorServiceResponse(error)
    })
}

//#endregion

//#region ADD

export const addNomination = async (nomination: any): Promise<ServiceResponse<any>> => {
  nomination.state = EnumOperationState.Borrador
  return await apiNominacionesPOST(
    nominationMapperPOST(nomination, getActiveBalanceZone(), getActiveCompany())
  )
    .then(response => {
      return createSuccessServiceResponse(response)
    })
    .catch(error => {
      return createErrorServiceResponse(error)
    })
}

export const addMassive = async (
  createOrUpdateMassiveData: AddMassiveNominationModel
): Promise<ServiceBatchResponse> => {
  createOrUpdateMassiveData.state = EnumOperationState.Borrador
  return await apiCreateOrUpdateMassiveOps(
    mapCreateOrUpdateMassiveRequest(
      createOrUpdateMassiveData,
      getActiveCompany()?.id!,
      getActiveBalanceZone()?.id!
    )
  )
    .then((response: HandleBatchResponse) => {
      console.log(response)
      if (!response.success) {
        return createPioneerErrorServiceBatchResponse(response.errors)
      }
      return createSuccessServiceBatchResponse()
    })
    .catch(error => {
      return createGenericErrorServiceBatchResponse(error)
    })
}

//#endregion

//#region Operation global actions

export const filterApplicableActionsConfiguration = (
  item: NominationModel,
  configurations: Array<DataTableActionConfiguration>
): Array<DataTableActionConfiguration> => {
  let applicableActionsConfiguration: Array<DataTableActionConfiguration> = []
  configurations.forEach(configuration => {
    switch (configuration.icon) {
      case ActionIcons.WARNING: {
        if (
          ((item.sendQuantity && item.quantity !== item.sendQuantity) ||
            (item.sendQuantityTSO && item.quantity !== item.sendQuantityTSO)) &&
          (item.state === EnumOperationState.PendienteRenvio ||
            item.state === EnumOperationState.Borrador ||
            item.state === EnumOperationState.Rechazado ||
            item.state === EnumOperationState.EnviadoKO)
        ) {
          applicableActionsConfiguration.push(configuration)
        }
        break
      }
      case ActionIcons.ERROR: {
        if (
          ((item.sendQuantity && item.quantity !== item.sendQuantity) ||
            (item.sendQuantityTSO && item.quantity !== item.sendQuantityTSO)) &&
          item.state === EnumOperationState.Enviada
        ) {
          applicableActionsConfiguration.push(configuration)
        }
        break
      }
      case ActionIcons.FLANGES: {
        if (item.flanges && item.flanges.length > 0) {
          applicableActionsConfiguration.push(configuration)
        }
        break
      }
    }
  })

  return applicableActionsConfiguration
}

export const getGlobalActionsConfiguration = (): Array<DataTableActionConfiguration> => {
  return [
    getQuantityDisagreementAction(),
    getStateDisagreementAction(),
    getNominationFlangesAction()
  ]
}

const getQuantityDisagreementAction = (): DataTableActionConfiguration => {
  return {
    icon: ActionIcons.WARNING,
    tooltip: i18n.t('nomination:dataTable.tooltips.quantityDisagreement')
  }
}

const getStateDisagreementAction = (): DataTableActionConfiguration => {
  return {
    icon: ActionIcons.ERROR,
    tooltip: i18n.t('nomination:dataTable.tooltips.stateDisagreement')
  }
}

const getNominationFlangesAction = (): DataTableActionConfiguration => {
  return {
    icon: ActionIcons.FLANGES,
    tooltip: i18n.t('nomination:dataTable.tooltips.hasFlanges')
  }
}

//#endregion

//#region Retries

export const checkOperationsState = async (
  operationIds: any,
  desiredStates: EnumOperationState[]
): Promise<NominationModel[]> => {
  return await apiNominacionesGETById(operationIds).then(response => {
    let updatedOperations: Operacion[] = []
    response.forEach(op => {
      if (desiredStates.some(x => x === enumValueToKey(EnumOperationState, op.estadoOperacion))) {
        updatedOperations.push(op)
      }
    })
    return []
  })
}

//#endregion
